import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';

import type { Appointment } from '@/react-query/useAppointments';
import {
  useReferralDetails,
  useReferralInfo,
} from '@/react-query/useReferralInfo';
import { useAppContext } from '@/utils/hooks/useAppContext';
import { parseDate } from '@/utils/parseDate';

import { ReferralForm } from './components/ReferralForm';

interface HuddleReferralModalProps {
  appointment: Appointment;
  isOpen: boolean;
  onClose: () => void;
}

export const HuddleReferralModal = ({
  appointment,
  isOpen,
  onClose,
}: HuddleReferralModalProps) => {
  const { user } = useAppContext();

  // Get practice and patient information
  const { data: practiceInfo, isLoading: isLoadingPracticeInfo } =
    useReferralInfo(
      user?.primaryPracticeId || '',
      appointment.patient?.pmsPatientId,
      appointment.patient?.imagingPatientId,
      {
        enabled:
          isOpen &&
          !!user?.primaryPracticeId &&
          (!!appointment.patient?.pmsPatientId ||
            !!appointment.patient?.imagingPatientId),
      },
    );

  // Get existing referral details
  const { data: referralInfo, isLoading: isLoadingReferralInfo } =
    useReferralDetails(appointment.appointment.unifiedId, {
      enabled: isOpen && !!appointment.appointment.unifiedId,
    });

  const isLoading = isLoadingPracticeInfo || isLoadingReferralInfo;

  // Parse patient date of birth from practice info
  const parsePatientDOB = () => {
    if (practiceInfo?.patient?.dateOfBirth) {
      const dob = parseDate(practiceInfo.patient.dateOfBirth);
      return {
        day: dob.getDate().toString().padStart(2, '0'),
        month: (dob.getMonth() + 1).toString().padStart(2, '0'),
        year: dob.getFullYear().toString(),
      };
    }
    return { day: '', month: '', year: '' };
  };

  const patientDOB = parsePatientDOB();

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="vai-max-w-[1000px] vai-p-4">
        <DialogHeader className="vai-pb-2">
          <DialogTitle>
            <Trans>Send Referral</Trans>
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="vai-flex vai-items-center vai-justify-center vai-h-[400px]">
            <FontAwesomeIcon
              icon={faSpinner}
              className="vai-h-8 vai-w-8 vai-text-primary vai-animate-spin"
            />
          </div>
        ) : (
          <ReferralForm
            onCancel={onClose}
            isLoading={isLoading}
            patientId={appointment.patient?.imagingPatientId || ''}
            videaPracticeId={String(user?.primaryPracticeId || '')}
            mode="appointment"
            unifiedAppointmentId={appointment.appointment.unifiedId}
            referralId={referralInfo?.id}
            onSubmitSuccess={() => {
              // Referral submitted successfully from huddle modal
            }}
            defaultValues={{
              patientFirstName:
                referralInfo?.patient_first_name ||
                practiceInfo?.patient?.firstName ||
                '',
              patientLastName:
                referralInfo?.patient_last_name ||
                practiceInfo?.patient?.lastName ||
                '',
              patientEmail:
                referralInfo?.patient_email ||
                practiceInfo?.patient?.email ||
                '',
              patientPhone:
                referralInfo?.patient_phone ||
                practiceInfo?.patient?.phone ||
                '',
              patientDOBYear: patientDOB.year,
              patientDOBMonth: patientDOB.month,
              patientDOBDay: patientDOB.day,
              doctorFirstName: practiceInfo?.provider?.firstName || '',
              doctorLastName: practiceInfo?.provider?.lastName || '',
              practiceEmail: practiceInfo?.practiceEmail || '',
              doctorPhone: practiceInfo?.practicePhoneNumber || '',
              specialtyId: referralInfo?.specialty_id,
              specialtyName: referralInfo?.specialty_name,
              proceduresIds: referralInfo?.procedures_ids || [],
              referralPracticeId: referralInfo?.referral_practice_id,
              referralSpecialistId: referralInfo?.referral_specialist_id,
            }}
            appointment={appointment}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
